import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Briefcase,
  CheckCircle,
  ChevronRight,
  GraduationCap,
  Lock,
  Sparkles,
  Terminal,
  User,
  Users,
  X,
  Zap
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from './HomePageLab';


const ECHANGE_LABELS = {
  ecole: {
    pesacore: "Accès au Protocole PESACORE · Simulations calibrées · Certifications co-signées",
    partenaire: "Visibilité institutionnelle · Accès à une cohorte de recherche · Relais académique officiel",
  },
  entreprise: {
    pesacore: "Protocole PESACORE · ROI documenté et mesurable · Analytics de transformation",
    partenaire: "Fondation d'entreprise active · Mécénat de compétences · Terrain pilote labellisé",
  },
  insertion: {
    pesacore: "Protocole PESACORE complet · 50% de remise en mouvement observé dès 2011 · Protocole terrain validé FSE",
    partenaire: "Financement FSE / OPCO / Région · Terrain institutionnel · Public réel · Légitimité territoriale",
  },
  cabinet: {
    pesacore: "Accès au Protocole · Co-branding encadré · Accompagnement à la montée en compétence",
    partenaire: "Réseau de prescription actif · Cas terrain documentés · Témoignages clients exploitables",
  },
  individuel: {
    pesacore: "Accès au Protocole PESACORE · Accompagnement dédié au déploiement",
    partenaire: "Retours de terrain structurés · Co-construction de contenus sectoriels",
  },
};



const PLUGINS_TEXTS = {

  ecole: {
    gardeFou: {
      message: "Les établissements qui souhaitent intégrer PESACORE dans leurs cours disposent de l'offre Academia, conçue précisément pour ce cas. Ce canal de partenariat s'adresse aux structures capables d'apporter une contrepartie institutionnelle structurée — visibilité, accès à une cohorte de recherche ou relais officiel. Si votre besoin est avant tout pédagogique avec un budget dédié, l'offre Academia est le chemin le plus direct.",
    },
    steps: {
      qualification: {
        info: "Un partenariat institutionnel n'est pas un abonnement négocié. Chaque partie apporte quelque chose que l'argent seul ne peut pas acheter — une légitimité, un accès, un réseau. Cette première étape permet de qualifier la nature réelle de l'échange envisagé.",
      },
      contrepartie: {
        info: "La solidité d'un partenariat repose sur la clarté de ce que chaque partie s'engage à apporter. Plus l'engagement est précis dès cette étape, plus la relation est durable. Indiquez votre contribution principale — elle déterminera la structure de l'accord.",
      },
      contexte: {
        info: "Ces informations permettent d'adapter le déploiement à votre réalité terrain. Un lycée de 200 apprenants et une grande école de 1 200 étudiants n'appellent pas le même protocole, même si la méthodologie reste identique.",
      },
      transmission: {
        info: "Votre dossier sera traité sous 48 heures ouvrées. Un premier échange de trente minutes vous sera proposé — l'objectif n'est pas de vous convaincre, mais de vérifier ensemble que les intentions sont convergentes.",
      },
    },
  },

  entreprise: {
    gardeFou: {
      message: "Les entreprises qui cherchent à renforcer leurs équipes, réduire leur turnover ou développer les compétences de leurs collaborateurs disposent de l'offre Pro, construite pour répondre à ces besoins. Ce canal de partenariat est réservé aux Fondations d'entreprise, pôles RSE et structures de mécénat qui apportent une valeur non-commerciale — financement de cohortes tierces, mécénat de compétences, terrain d'expérimentation sociale.",
    },
    steps: {
      qualification: {
        info: "Une même entreprise peut être cliente PESACORE pour la formation de ses équipes, et disposer d'une Fondation partenaire pour ses actions d'impact social. Ces deux relations sont distinctes, gérées séparément, et ne se substituent pas l'une à l'autre. Précisez ici au nom de quelle structure vous intervenez.",
      },
      contrepartie: {
        info: "L'échange doit être à la fois symétrique et mesurable. PESACORE déploie son protocole auprès d'une cohorte définie — la Fondation ou structure RSE finance et apporte la visibilité institutionnelle. La valeur produite par chaque partie est documentée et consultable.",
      },
      transmission: {
        info: "Un premier échange de trente minutes vous sera proposé sous 48 heures. Venez avec ce que vous avez — pas besoin de dossier formalisé à ce stade. La conversation suffit pour établir si les conditions d'un partenariat sérieux sont réunies.",
      },
    },
  },

  insertion: {
    gardeFou: null,
    steps: {
      modele_financement: {
        info: "Depuis 2011 et la Mission Locale de l'Orléanais — premier déploiement terrain validé par la DREETS Centre-Val de Loire dans le cadre du FSE — ce modèle a fait ses preuves : un financement tiers prend le relais de la contrepartie monétaire directe. Le terrain, lui, ne s'achète pas. Indiquez ici le dispositif de financement que vous pouvez mobiliser.",
      },
      contrepartie: {
        info: "Au-delà du financement, votre structure apporte quelque chose d'irremplaçable : un public réel, une légitimité institutionnelle et un réseau de prescription. C'est cette combinaison — méthode + terrain + financement — qui a permis d'atteindre 50% de remise en mouvement dès le premier projet dans les cohortes pilotes.",
      },
      dispositif: {
        info: "La méthodologie PESACORE s'intègre dans les parcours existants sans les remplacer intégralement. Elle peut fonctionner en complément d'un dispositif actif, en remplacement d'un module insuffisamment efficace, ou comme dispositif autonome. Précisez comment vous envisagez cette intégration pour que le déploiement soit calibré à votre réalité.",
      },
      transmission: {
        info: "Votre dossier sera examiné sous 48 heures. Si le cadre est cohérent, un échange de trente minutes vous sera proposé pour poser les bases du partenariat. Aucune décision n'est prise avant cet échange.",
      },
    },
  },

  cabinet: {
    gardeFou: {
      message: "Un partenariat cabinet PESACORE repose sur un déploiement actif du protocole — pas sur une revente de licences ou une prescription passive. Ce canal est ouvert aux praticiens et consultants qui co-construisent, animent et s'engagent sur les résultats terrain. Si votre modèle est avant tout de la prescription sans implication directe dans le déploiement, ce canal ne correspond pas à ce que nous cherchons à construire.",
    },
    steps: {
      qualification: {
        info: "Ce que vous apportez ici, c'est du terrain et de la prescription active — une valeur que l'argent seul ne peut pas produire. La qualité d'un partenariat cabinet se mesure à la rigueur du déploiement, pas au volume de prescriptions brutes.",
      },
      perimetre: {
        info: "Un partenariat sérieux se construit sur des engagements réels, pas sur des intentions. Le volume que vous indiquez ici n'est pas un critère de sélection — c'est une base de travail pour calibrer l'accompagnement que PESACORE vous apportera en retour.",
      },
      transmission: {
        info: "Un premier échange sera proposé sous 48 heures. L'objectif est simple : vérifier ensemble que vos modalités d'intervention sont compatibles avec ce que le protocole exige. La réponse est toujours directe, dans un sens ou dans l'autre.",
      },
    },
  },

  individuel: {
    gardeFou: {
      message: "Si vous souhaitez accéder à PESACORE pour votre propre parcours de développement personnel, l'offre Particulier est construite pour cela. Ce canal de partenariat praticien s'adresse exclusivement aux professionnels qui déploient le protocole auprès d'autres personnes — clients, apprenants ou organisations — et qui sont en mesure d'en documenter les résultats.",
    },
    steps: {
      qualification: {
        info: "Le partenariat praticien est un échange concret et équilibré : PESACORE vous ouvre un accès complet à son protocole — vous apportez du terrain réel, des retours structurés et contribuez à l'amélioration continue de la méthodologie. Ce n'est pas un accès gratuit — c'est un accès différemment valorisé.",
      },
      contrepartie: {
        info: "L'engagement minimum est de six mois. Ce que vous apportez pendant cette période compte autant que ce que vous recevez. Les retours de terrain structurés que vous fournirez alimentent l'évolution du protocole — ils ont une valeur réelle pour l'écosystème.",
      },
      transmission: {
        info: "Votre profil sera examiné sous 48 heures. Si votre pratique est compatible avec ce que le partenariat praticien exige, un échange de trente minutes vous sera proposé pour poser les bases de la collaboration.",
      },
    },
  },
};

const PLUGINS = [
  {
    id: 'ecole',
    code: 'EDU',
    label: 'École / Établissement',
    icon: GraduationCap,
    accent: 'cyan',
    accentHex: '#22d3ee',
    accentBg: 'rgba(34,211,238,0.06)',
    accentBorder: 'rgba(34,211,238,0.2)',
    desc: 'Un partenariat pédagogique fondé sur l\'échange — visibilité académique et accès à un cadre qui transforme les parcours.',
    protocol: 'EDU-EXCHANGE v2.4',
    gardeFou: {
      trigger: 'budget_absent',
      message: "Les établissements qui souhaitent intégrer PESACORE dans leurs cours disposent de l'offre Academia, conçue précisément pour ce cas. Ce canal de partenariat s'adresse aux structures capables d'apporter une contrepartie institutionnelle structurée — visibilité, accès à une cohorte de recherche ou relais officiel. Si votre besoin est avant tout pédagogique avec un budget dédié, l'offre Academia est le chemin le plus direct.",
      redirectLabel: "Découvrir l'offre Academia",
      redirectPath: '/education',
    },
    steps: [
      {
        id: 'qualification',
        titre: 'Ce que vous envisagez',
        info: "Un partenariat n'est pas un abonnement négocié. Chaque partie apporte quelque chose que l'argent seul ne peut pas acheter.",
        fields: [
          {
            id: 'nature',
            label: 'Quelle relation souhaitez-vous construire ?',
            type: 'toggle',
            options: [
              'Accès à une cohorte pour une recherche terrain',
              'Visibilité de PESACORE sur vos supports de communication',
              'Relais institutionnel et recommandation officielle',
              'Co-construction de contenus pédagogiques',
            ],
            gardeFou: null,
          },
          {
            id: 'budget_situation',
            label: 'Quelle est votre situation budgétaire ?',
            type: 'toggle',
            options: [
              'Nous avons un budget formation — abonnement Academia',
              'Financement partiel envisageable avec contrepartie',
              'Pas de budget disponible — contrepartie institutionnelle',
            ],
            gardeFou: 'Nous avons un budget formation — abonnement Academia',
          },
        ],
      },
      {
        id: 'contrepartie',
        titre: 'Ce que vous apportez',
        info: "La solidité d'un partenariat tient à ce que chaque partie s'engage à apporter. Précisez votre contribution.",
        fields: [
          {
            id: 'apport',
            label: 'Votre apport principal',
            type: 'select',
            options: [
              'Logo PESACORE sur vos supports officiels (plaquettes, site)',
              'Accès à une cohorte pour une étude d\'impact',
              'Co-signature d\'une publication académique',
              'Relais vers votre réseau d\'établissements',
              'Mise à disposition d\'espaces et d\'infrastructure',
            ],
          },
          {
            id: 'duree_engagement',
            label: 'Durée d\'engagement souhaitée',
            type: 'toggle',
            options: ['1 an', '2 ans', '3 ans et plus'],
          },
        ],
      },
      {
        id: 'contexte',
        titre: 'Votre établissement',
        fields: [
          {
            id: 'type',
            label: 'Type d\'établissement',
            type: 'select',
            options: ['Lycée / CFA', 'BTS / IUT', 'Grande École', 'Université', 'Centre de formation'],
          },
          {
            id: 'effectif',
            label: 'Effectif concerné',
            type: 'range',
            min: 30, max: 600, step: 30, unit: 'étudiants',
          },
        ],
      },
      {
        id: 'transmission',
        titre: 'Pour vous recontacter',
        fields: [
          { id: 'identifiant', label: 'Votre adresse institutionnelle', type: 'text', placeholder: 'direction@etablissement.fr' },
          { id: 'organisation', label: 'Nom de l\'établissement', type: 'text', placeholder: 'Lycée Jean Moulin — Orléans' },
        ],
      },
    ],
  },

  {
    id: 'entreprise',
    code: 'ENT',
    label: 'Entreprise / Fondation',
    icon: Briefcase,
    accent: 'orange',
    accentHex: '#f97316',
    accentBg: 'rgba(249,115,22,0.06)',
    accentBorder: 'rgba(249,115,22,0.2)',
    desc: 'Une entreprise peut être cliente de PESACORE et avoir une Fondation partenaire — deux relations distinctes, deux canaux différents.',
    protocol: 'ENT-EXCHANGE v3.1',
    gardeFou: {
      trigger: 'canal_commercial',
      message: "Les entreprises qui cherchent à renforcer leurs équipes ou réduire leur turnover ont accès à l'offre Pro, pensée pour ça. Ce canal est ouvert aux Fondations, structures RSE et mécènes qui apportent une valeur non-commerciale.",
      redirectLabel: "Découvrir l'offre Entreprise",
      redirectPath: '/entreprise',
    },
    steps: [
      {
        id: 'qualification',
        titre: 'Qui vous représentez',
        info: "Une même entreprise peut être cliente PESACORE pour ses équipes, et avoir une Fondation partenaire pour ses actions d'impact. Ces deux relations vivent séparément.",
        fields: [
          {
            id: 'canal',
            label: 'Vous intervenez au nom de',
            type: 'toggle',
            options: [
              'La Direction ou la DRH — besoin de formation',
              'La Fondation d\'entreprise ou le pôle RSE',
              'Un département mécénat ou engagement sociétal',
            ],
            gardeFou: 'La Direction ou la DRH — besoin de formation',
          },
          {
            id: 'type_echange',
            label: 'Nature de l\'échange envisagé',
            type: 'toggle',
            options: [
              'Mécénat de compétences — mise à disposition RH',
              'Financement d\'une cohorte insertion tierce',
              'Co-branding sur un programme d\'impact social',
              'Terrain pilote auprès d\'un public vulnérable',
            ],
          },
        ],
      },
      {
        id: 'contrepartie',
        titre: 'La structure de l\'échange',
        info: "L'échange doit être symétrique et mesurable. PESACORE déploie son paradigme sur une cohorte — la Fondation finance et apporte sa visibilité.",
        fields: [
          {
            id: 'apport_entreprise',
            label: 'Ce que votre structure apporte',
            type: 'select',
            options: [
              'Financement direct d\'une cohorte (FSE, mécénat)',
              'Mise à disposition de collaborateurs — mécénat compétences',
              'Présence de PESACORE dans vos rapports RSE et communications',
              'Accès à un terrain d\'expérimentation et à un public cible',
              'Co-signature de publications d\'impact',
            ],
          },
          {
            id: 'perimetre',
            label: 'Périmètre de la cohorte concernée',
            type: 'toggle',
            options: ['Public insertion / jeunes NEET', 'Collaborateurs en reconversion', 'Public externe — territoire', 'À définir ensemble'],
          },
        ],
      },
      {
        id: 'transmission',
        titre: 'Pour vous recontacter',
        fields: [
          { id: 'identifiant', label: 'Votre adresse Fondation ou RSE', type: 'text', placeholder: 'fondation@groupe.com' },
          { id: 'organisation', label: 'Nom de la Fondation ou structure', type: 'text', placeholder: 'Fondation Michelin — Direction Impact' },
        ],
      },
    ],
  },

  {
    id: 'insertion',
    code: 'INS',
    label: 'Insertion / OPCO',
    icon: Users,
    accent: 'emerald',
    accentHex: '#10b981',
    accentBg: 'rgba(16,185,129,0.06)',
    accentBorder: 'rgba(16,185,129,0.2)',
    desc: 'Le partenariat de référence — financement FSE, OPCO ou Région en contrepartie d\'un déploiement terrain. 50% de remise en mouvement observé depuis 2011.',
    protocol: 'INS-EXCHANGE v1.8',
    gardeFou: null,
    steps: [
      {
        id: 'modele_financement',
        titre: 'Le financement mobilisable',
        info: "Depuis 2011 et la Mission Locale de l'Orléanais, ce modèle a fait ses preuves : un financement tiers — FSE, OPCO, Région — prend le relais de la contrepartie monétaire directe. Le terrain, lui, n'a pas de prix.",
        fields: [
          {
            id: 'financement',
            label: 'Quel financement pouvez-vous mobiliser ?',
            type: 'toggle',
            options: [
              'FSE / FRR — Fonds Social Européen',
              'OPCO ou financement de la formation',
              'Région ou Département',
              'Fondation privée ou mécénat',
              'Fonds propres de la structure avec contrepartie',
            ],
          },
          {
            id: 'public',
            label: 'Qui accompagnez-vous ?',
            type: 'toggle',
            options: ['Jeunes NEET 16–25 ans', 'Demandeurs d\'emploi longue durée', 'Bénéficiaires du RSA', 'Publics mixtes'],
          },
        ],
      },
      {
        id: 'contrepartie',
        titre: 'Ce que vous apportez',
        info: "Au-delà du financement, votre structure apporte quelque chose d'irremplaçable — un public réel, une légitimité institutionnelle, un réseau de prescription.",
        fields: [
          {
            id: 'apport',
            label: 'Votre apport principal',
            type: 'select',
            options: [
              'Accès direct au public cible — orientation et suivi',
              'Légitimité institutionnelle et recommandation officielle',
              'Infrastructure d\'accueil — locaux et matériel',
              'Réseau de prescripteurs — MLO, CAF, France Travail…',
              'Co-portage d\'un dossier de financement',
            ],
          },
          {
            id: 'volume',
            label: 'Volume annuel de bénéficiaires',
            type: 'range',
            min: 20, max: 300, step: 20, unit: 'bénéficiaires / an',
          },
        ],
      },
      {
        id: 'dispositif',
        titre: 'L\'intégration dans votre parcours',
        fields: [
          {
            id: 'integration',
            label: 'Comment envisagez-vous l\'intégration ?',
            type: 'toggle',
            options: ['En complément d\'un dispositif actif', 'En remplacement d\'un module existant', 'Dispositif autonome financé'],
          },
          {
            id: 'mesure',
            label: 'Quel résultat souhaitez-vous mesurer ?',
            type: 'toggle',
            options: ['Taux de remise en mouvement', 'Accès à l\'emploi ou à la formation', 'Progression des compétences socles', 'Rapport d\'impact co-signé'],
          },
        ],
      },
      {
        id: 'transmission',
        titre: 'Pour vous recontacter',
        fields: [
          { id: 'identifiant', label: 'Votre adresse institutionnelle', type: 'text', placeholder: 'contact@structure-insertion.fr' },
          { id: 'organisation', label: 'Nom de la structure', type: 'text', placeholder: 'Mission Locale du Loiret' },
        ],
      },
    ],
  },

  {
    id: 'cabinet',
    code: 'CAB',
    label: 'Cabinet / Consultant',
    icon: Terminal,
    accent: 'violet',
    accentHex: '#8b5cf6',
    accentBg: 'rgba(139,92,246,0.06)',
    accentBorder: 'rgba(139,92,246,0.2)',
    desc: 'Intégrer le paradigme PESACORE dans vos missions — un échange structuré, pas une simple licence à revendre.',
    protocol: 'CAB-EXCHANGE v2.0',
    gardeFou: {
      trigger: 'revente_passive',
      message: "Un partenariat cabinet repose sur un déploiement actif du paradigme, pas sur une revente de licences. Ce canal est ouvert aux praticiens qui co-construisent et s'engagent sur le terrain.",
      redirectLabel: null,
      redirectPath: null,
    },
    steps: [
      {
        id: 'qualification',
        titre: 'Comment vous intervenez',
        info: "Ce que vous apportez ici, c'est du terrain et de la prescription — une valeur que l'argent seul ne peut pas produire.",
        fields: [
          {
            id: 'mode',
            label: 'Comment envisagez-vous d\'intervenir ?',
            type: 'toggle',
            options: [
              'Déploiement direct chez vos clients — vous animez',
              'Co-animation avec l\'équipe PESACORE',
              'Prescription et accompagnement post-déploiement',
            ],
            gardeFou: null,
          },
          {
            id: 'contrepartie',
            label: 'Ce que vous apportez à PESACORE',
            type: 'toggle',
            options: [
              'Cas terrain documentés et témoignages clients',
              'Réseau de prescription actif — 5 prescriptions minimum par an',
              'Co-construction de contenus sectoriels',
              'Visibilité de PESACORE dans vos communications',
            ],
          },
        ],
      },
      {
        id: 'perimetre',
        titre: 'Le périmètre de votre engagement',
        info: "Un partenariat sérieux commence par un volume d'engagement réel, pas une intention.",
        fields: [
          {
            id: 'volume',
            label: 'Déploiements engagés en première année',
            type: 'range',
            min: 3, max: 50, step: 1, unit: 'déploiements / an',
          },
          {
            id: 'domaine',
            label: 'Votre domaine d\'intervention principal',
            type: 'select',
            options: ['Conseil en organisation', 'Formation et ingénierie pédagogique', 'Coaching et développement', 'RH et recrutement', 'Insertion et ESS', 'Autre domaine'],
          },
        ],
      },
      {
        id: 'transmission',
        titre: 'Pour vous recontacter',
        fields: [
          { id: 'identifiant', label: 'Votre adresse professionnelle', type: 'text', placeholder: 'contact@cabinet.fr' },
          { id: 'organisation', label: 'Nom du cabinet ou de la structure', type: 'text', placeholder: 'Cabinet Dynamics — Paris' },
        ],
      },
    ],
  },

  {
    id: 'individuel',
    code: 'IND',
    label: 'Praticien / Freelance',
    icon: User,
    accent: 'amber',
    accentHex: '#f59e0b',
    accentBg: 'rgba(245,158,11,0.06)',
    accentBorder: 'rgba(245,158,11,0.2)',
    desc: 'Accéder au paradigme PESACORE pour l\'intégrer dans votre pratique — en échange de retours de terrain structurés.',
    protocol: 'IND-EXCHANGE v1.5',
    gardeFou: {
      trigger: 'usage_personnel',
      message: "Si vous souhaitez accéder à PESACORE pour votre propre parcours, l'offre Particulier est faite pour ça. Ce canal s'adresse aux praticiens qui déploient le paradigme auprès d'autres personnes.",
      redirectLabel: "Découvrir l'offre Particulier",
      redirectPath: '/auth/register?plan=particulier',
    },
    steps: [
      {
        id: 'qualification',
        titre: 'Votre pratique',
        info: "Le partenariat praticien est un échange concret : PESACORE vous ouvre l'accès à son paradigme — vous apportez du terrain, des retours et contribuez à son évolution.",
        fields: [
          {
            id: 'activite',
            label: 'Comment qualifiez-vous votre activité ?',
            type: 'select',
            options: ['Formateur indépendant', 'Coach professionnel', 'Consultant solo', 'Enseignant vacataire ou chercheur', 'Praticien insertion et social'],
          },
          {
            id: 'usage_cible',
            label: 'Vous souhaitez déployer PESACORE auprès de',
            type: 'toggle',
            options: [
              'Vos bénéficiaires — clients ou apprenants',
              'Une organisation que vous accompagnez',
            ],
            gardeFou: null,
          },
        ],
      },
      {
        id: 'contrepartie',
        titre: 'Votre contribution',
        info: "L'engagement minimum est de 6 mois. Ce que vous apportez compte autant que ce que vous recevez.",
        fields: [
          {
            id: 'apport',
            label: 'Ce que vous êtes prêt à apporter',
            type: 'toggle',
            options: [
              'Rapports de terrain structurés à chaque déploiement',
              'Témoignages de bénéficiaires exploitables',
              'Co-construction d\'un contenu sectoriel',
              'Prescription active dans votre réseau',
            ],
          },
          {
            id: 'volume',
            label: 'Volume de personnes accompagnées',
            type: 'range',
            min: 5, max: 100, step: 5, unit: 'personnes / an',
          },
        ],
      },
      {
        id: 'transmission',
        titre: 'Pour vous recontacter',
        fields: [
          { id: 'identifiant', label: 'Votre adresse professionnelle', type: 'text', placeholder: 'votre@email.pro' },
          { id: 'organisation', label: 'Votre nom et structure', type: 'text', placeholder: 'Jean Dupont — Formateur indépendant' },
        ],
      },
    ],
  },
];

const PARTENAIRES_ENGAGES = [
  { code: 'INS-003', label: 'Structure insertion — Loiret', statut: 'Actif', phase: 'Évaluation résultats', depuis: '2023', echange: 'FSE + terrain MLO' },
  { code: 'EDU-012', label: 'Établissement — Centre Val-de-Loire', statut: 'Actif', phase: 'Déploiement P2', depuis: '2024', echange: 'Cohorte recherche' },
  { code: 'ENT-007', label: 'Fondation — Secteur industrie', statut: 'Actif', phase: 'Pilote en cours', depuis: '2025', echange: 'Mécénat compétences' },
];


// ─── Données Pesas ────────────────────────────────────────────────────────────
const PESAS_USAGES = [
  { label: 'Accéder à une formation complémentaire', icon: '📚', who: 'Apprenant', flow: 'dépense' },
  { label: 'Financer un stage chez un partenaire', icon: '🤝', who: 'Apprenant', flow: 'dépense' },
  { label: 'Obtenir une réduction durable sur un parcours', icon: '🎓', who: 'Apprenant', flow: 'dépense' },
  { label: 'Recevoir un profil qualifié en stage', icon: '⭐', who: 'Partenaire', flow: 'reçoit' },
  { label: 'Valoriser un mécénat de compétences', icon: '🏛️', who: 'Partenaire', flow: 'reçoit' },
  { label: 'Accéder aux opérations spéciales profils', icon: '🔭', who: 'Partenaire', flow: 'reçoit' },
];

const PESAS_COMMENT_OBTENIR = [
  { action: 'Compléter un module ou une simulation', gain: '+5 à +20 Pesas' },
  { action: 'Valider une compétence certifiée', gain: '+30 Pesas' },
  { action: 'Atteindre un seuil de progression cohorte', gain: '+50 Pesas' },
  { action: 'Être mis en avant par un partenaire', gain: 'Variable' },
  { action: 'Inscription — offerts par les fondateurs', gain: '220 Pesas' },
];

// ─── Composant champ wizard ───────────────────────────────────────────────────
function WizardField({ field, value, onChange, accent, onGardeFouTrigger }) {
  const accentColor = {
    cyan:    { active: 'bg-cyan-500/15 border-cyan-500/50 text-cyan-300',          inactive: 'bg-zinc-900/60 border-zinc-700/40 text-zinc-300 hover:border-zinc-500' },
    orange:  { active: 'bg-orange-500/15 border-orange-500/50 text-orange-300',    inactive: 'bg-zinc-900/60 border-zinc-700/40 text-zinc-300 hover:border-zinc-500' },
    emerald: { active: 'bg-emerald-500/15 border-emerald-500/50 text-emerald-300', inactive: 'bg-zinc-900/60 border-zinc-700/40 text-zinc-300 hover:border-zinc-500' },
    violet:  { active: 'bg-violet-500/15 border-violet-500/50 text-violet-300',    inactive: 'bg-zinc-900/60 border-zinc-700/40 text-zinc-300 hover:border-zinc-500' },
    amber:   { active: 'bg-amber-500/15 border-amber-500/50 text-amber-300',       inactive: 'bg-zinc-900/60 border-zinc-700/40 text-zinc-300 hover:border-zinc-500' },
  }[accent] || {};

  const handleToggle = (opt) => {
    if (field.gardeFou && opt === field.gardeFou) {
      onGardeFouTrigger && onGardeFouTrigger();
      return;
    }
    onChange(field.id, opt);
  };

  if (field.type === 'toggle') {
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-100 mb-2">{field.label}</div>
        <div className="flex flex-wrap gap-2">
          {field.options.map(opt => {
            const isGardeFou = field.gardeFou && opt === field.gardeFou;
            return (
              <button
                key={opt}
                onClick={() => handleToggle(opt)}
                className={`px-3 py-1.5 border rounded-lg text-xs font-mono transition-all ${
                  value === opt
                    ? accentColor.active
                    : isGardeFou
                      ? 'bg-zinc-900/60 border-zinc-800/40 text-zinc-200 hover:border-amber-500/30 hover:text-amber-400/70'
                      : accentColor.inactive
                }`}
              >
                {isGardeFou ? `↗ ${opt}` : opt}
              </button>
            );
          })}
        </div>
        {field.gardeFou && (
          <p className="text-[9px] font-mono text-zinc-200 italic mt-1">
            ↗ Cette option vous oriente vers le canal le mieux adapté à votre situation
          </p>
        )}
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-100 mb-2">{field.label}</div>
        <div className="grid grid-cols-2 gap-1.5">
          {field.options.map(opt => (
            <button
              key={opt}
              onClick={() => onChange(field.id, opt)}
              className={`px-3 py-2 border rounded-lg text-xs font-mono text-left transition-all ${value === opt ? accentColor.active : accentColor.inactive}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'range') {
    const val = value || field.min;
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-100">{field.label}</div>
          <div className={`text-sm font-black font-mono ${accentColor.active.split(' ').find(c => c.startsWith('text-'))}`}>
            {val} <span className="text-[9px] text-zinc-200 font-normal">{field.unit}</span>
          </div>
        </div>
        <input
          type="range" min={field.min} max={field.max} step={field.step}
          value={val}
          onChange={e => onChange(field.id, parseInt(e.target.value))}
          className="w-full h-1 bg-zinc-800 rounded appearance-none cursor-pointer"
          style={{ accentColor: { cyan: '#22d3ee', orange: '#f97316', emerald: '#10b981', violet: '#8b5cf6', amber: '#f59e0b' }[accent] }}
        />
        <div className="flex justify-between text-[9px] font-mono text-zinc-200">
          <span>{field.min}</span><span>{field.max}</span>
        </div>
      </div>
    );
  }

  if (field.type === 'text') {
    return (
      <div className="space-y-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-100 mb-2">{field.label}</div>
        <div className="flex items-center gap-2 px-3 py-2.5 bg-zinc-900/80 border border-zinc-700/50 rounded-lg transition-all focus-within:border-zinc-400">
          <span className=" text-zinc-300 font-mono text-xs">›</span>
          <input
            type="text"
            placeholder={field.placeholder}
            value={value || ''}
            onChange={e => onChange(field.id, e.target.value)}
            className="flex-1 bg-transparent text-sm font-mono text-zinc-200 placeholder: text-zinc-300 outline-none"
          />
        </div>
      </div>
    );
  }

  return null;
}

// ─── Écran garde-fou ──────────────────────────────────────────────────────────
function GardeFouScreen({ plugin, onClose, onContinue, navigate }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      className="flex flex-col items-center justify-center py-8 px-2 text-center gap-6"
    >
      <div className="w-14 h-14 rounded-full border border-amber-500/25 bg-amber-500/6 flex items-center justify-center">
        <AlertTriangle size={22} className="text-amber-400" />
      </div>

      <div className="max-w-xs">
        <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400/70 mb-3">
          Un autre chemin correspond mieux à votre situation
        </div>
        <p className="text-zinc-300 text-sm leading-relaxed">
          {plugin.gardeFou?.message}
        </p>
      </div>

      <div className="w-full border-t border-zinc-800/50 pt-5 space-y-2">
        {plugin.gardeFou?.redirectLabel && (
          <button
            onClick={() => { onClose(); navigate(plugin.gardeFou.redirectPath); }}
            className="w-full px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-xs font-mono text-amber-300 hover:bg-amber-500/20 transition-all flex items-center justify-center gap-2"
          >
            {plugin.gardeFou.redirectLabel}
            <ArrowRight size={11} />
          </button>
        )}
        <button
          onClick={onContinue}
          className="w-full px-4 py-2.5 border border-zinc-700/50 rounded-xl text-[10px] font-mono text-zinc-200 hover:text-zinc-300 hover:border-zinc-600 transition-all"
        >
          Notre situation est plus complexe — continuer quand même
        </button>
        <p className="text-[9px] font-mono text-zinc-300 italic">
          Vous pouvez poursuivre si votre cas ne correspond pas exactement aux options proposées. Votre dossier sera examiné au cas par cas.
        </p>
      </div>
    </motion.div>
  );
}

// ─── Wizard overlay ───────────────────────────────────────────────────────────
function PluginWizard({ plugin, onClose, navigate }) {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [transmitting, setTransmitting] = useState(false);
  const [showGardeFou, setShowGardeFou] = useState(false);

  const currentStep = plugin.steps[step];
  const totalSteps = plugin.steps.length;
  const isLast = step === totalSteps - 1;

  const accentTextMap = {
    cyan: 'text-cyan-400', orange: 'text-orange-400',
    emerald: 'text-emerald-400', violet: 'text-violet-400', amber: 'text-amber-400',
  };
  const accentBtnMap = {
    cyan:    'bg-cyan-500/15 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25',
    orange:  'bg-orange-500/15 border-orange-500/40 text-orange-300 hover:bg-orange-500/25',
    emerald: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25',
    violet:  'bg-violet-500/15 border-violet-500/40 text-violet-300 hover:bg-violet-500/25',
    amber:   'bg-amber-500/15 border-amber-500 text-amber-300 hover:bg-amber-500/25',
  };

  const handleChange = (fieldId, val) => {
    setValues(prev => ({ ...prev, [`${currentStep.id}_${fieldId}`]: val }));
  };

  const stepComplete = () => {
    return currentStep.fields.every(f => {
      if (f.type === 'range') return true;
      const val = values[`${currentStep.id}_${f.id}`];
      return val && val !== '';
    });
  };

  const handleNext = async () => {
    if (isLast) {
      setTransmitting(true);
      await new Promise(r => setTimeout(r, 1800));
      setTransmitting(false);
      setSubmitted(true);
    } else {
      setStep(s => s + 1);
    }
  };

  const echange = ECHANGE_LABELS[plugin.id];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(2,6,23,0.92)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ duration: 0.3 }}
        className="relative w-full max-w-xl bg-zinc-950 border border-zinc-800/80 rounded-2xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Bande ENGINE LAB ── */}
        <div
          className="flex items-center justify-between px-5 py-3 border-b border-zinc-800/60"
          style={{ background: 'linear-gradient(90deg, rgba(245,158,11,0.07) 0%, rgba(9,9,11,0.9) 100%)' }}
        >
          <div className="flex items-center gap-3">
            <div className="h-3.5 w-px bg-amber-500/50" />
            <span className="font-mono text-[9px] text-amber-200/60 uppercase tracking-[0.2em]">ENGINE LAB</span>
            <div className="h-3.5 w-px bg-zinc-800" />
            <span className="font-mono text-[9px] text-zinc-200 uppercase tracking-widest">{plugin.protocol}</span>
          </div>
          <button onClick={onClose} className=" text-zinc-300 hover:text-zinc-300 transition-colors">
            <X size={14} />
          </button>
        </div>

        {/* ── Bandeau échange de valeur ── */}
        {!submitted && !showGardeFou && (
          <div
            className="px-5 py-3 border-b border-zinc-800/40 grid grid-cols-2 gap-4"
            style={{ background: 'rgba(9,9,11,0.6)' }}
          >
            <div>
              <div className="text-[8px] font-mono uppercase tracking-widest text-zinc-200 mb-1">PESACORE apporte</div>
              <div className="text-[10px] font-mono text-zinc-300 leading-relaxed">{echange.pesacore}</div>
            </div>
            <div>
              <div className="text-[8px] font-mono uppercase tracking-widest text-zinc-200 mb-1">Vous apportez</div>
              <div className={`text-[10px] font-mono leading-relaxed ${accentTextMap[plugin.accent]}`}>{echange.partenaire}</div>
            </div>
          </div>
        )}

        {/* ── Progression + info step ── */}
        {!submitted && !showGardeFou && (
          <div className="px-5 pt-4 pb-0">
            {/* Barre de progression */}
            <div className="flex items-center gap-1 mb-3">
              {plugin.steps.map((s, i) => (
                <div
                  key={i}
                  className={`h-0.5 flex-1 rounded transition-all duration-500 ${
                    i < step
                      ? 'opacity-100'
                      : i === step
                        ? 'opacity-60'
                        : 'bg-zinc-800 opacity-100'
                  }`}
                  style={i <= step ? { background: plugin.accentHex, opacity: i === step ? 0.6 : 1 } : {}}
                />
              ))}
            </div>

            {/* Titre de l'étape */}
            <div className="flex items-center justify-between mb-3">
              <span className={`font-mono text-[10px] uppercase tracking-widest ${accentTextMap[plugin.accent]}`}>
                {String(step + 1).padStart(2, '0')} / {String(totalSteps).padStart(2, '0')} — {currentStep.titre}
              </span>
              <span className="font-mono text-[9px] text-zinc-200">{plugin.code}</span>
            </div>

            {/* Info contextuelle — enrichie, en italique serif pour différencier du formulaire */}
            {currentStep.info && (
              <div className="mb-4 px-3 py-2.5 border-l-2 border-zinc-700/60 bg-zinc-900/20 rounded-r-lg">
                <p className="text-[11px] text-zinc-100 leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                  {currentStep.info}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Contenu principal ── */}
        <div className="px-5 pb-5">
          {showGardeFou ? (
            <GardeFouScreen
              plugin={plugin}
              onClose={onClose}
              onContinue={() => setShowGardeFou(false)}
              navigate={navigate}
            />
          ) : !submitted ? (
            <>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-5 mb-6 mt-1"
                >
                  {currentStep.fields.map(field => (
                    <WizardField
                      key={field.id}
                      field={field}
                      value={values[`${currentStep.id}_${field.id}`]}
                      onChange={handleChange}
                      accent={plugin.accent}
                      onGardeFouTrigger={() => setShowGardeFou(true)}
                    />
                  ))}
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center justify-between pt-4 border-t border-zinc-800/40">
                <button
                  onClick={() => step > 0 && setStep(s => s - 1)}
                  className={`text-[10px] font-mono uppercase tracking-wider transition-colors ${
                    step > 0 ? 'text-zinc-400 hover:text-zinc-200' : 'text-zinc-700 cursor-default'
                  }`}
                >
                  ← Précédent
                </button>
                <button
                  onClick={handleNext}
                  disabled={!stepComplete() || transmitting}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-mono uppercase tracking-wider border transition-all ${
                    stepComplete() && !transmitting
                      ? accentBtnMap[plugin.accent]
                      : 'bg-zinc-900/60 border-zinc-800/40 text-zinc-200 cursor-not-allowed'
                  }`}
                >
                  {transmitting ? (
                    <>
                      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}>
                        <Zap size={11} />
                      </motion.div>
                      Transmission en cours…
                    </>
                  ) : isLast ? (
                    <>Transmettre <Lock size={11} /></>
                  ) : (
                    <>Continuer <ChevronRight size={11} /></>
                  )}
                </button>
              </div>
            </>
          ) : (
            // ── Écran de confirmation enrichi ──
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-8 px-2"
            >
              <div
                className="w-14 h-14 mx-auto mb-5 rounded-full border flex items-center justify-center"
                style={{ borderColor: plugin.accentBorder, background: plugin.accentBg }}
              >
                <CheckCircle size={24} className={accentTextMap[plugin.accent]} />
              </div>

              <div className={`font-mono text-[10px] uppercase tracking-widest mb-2 ${accentTextMap[plugin.accent]}`}>
                Dossier transmis avec succès
              </div>
              <h3 className="text-white font-black text-xl mb-3">Bien reçu.</h3>

              <p className="text-zinc-400 text-sm leading-relaxed max-w-xs mx-auto mb-3">
                Votre dossier de qualification est en cours d'analyse. Vous recevrez une réponse sous 48 heures ouvrées.
              </p>
              <p className=" text-zinc-300 text-[11px] leading-relaxed max-w-xs mx-auto mb-6" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                Si les conditions sont réunies, un échange de trente minutes vous sera proposé pour poser les bases du partenariat. La qualification est mutuelle — aucune décision n'est prise avant cet échange.
              </p>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900/60 border border-zinc-800/40 rounded-xl">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="font-mono text-[10px] text-zinc-200 uppercase tracking-wider">
                  Réf. {plugin.code}-{Math.random().toString(36).substring(2, 7).toUpperCase()}
                </span>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function PartnersPage() {
  const navigate = useNavigate();
  const [activePlugin, setActivePlugin] = useState(null);
  const [hoveredPlugin, setHoveredPlugin] = useState(null);
  const [pesasTab, setPesasTab] = useState('comprendre');

  return (
    <div className="min-h-screen bg-[#020617] text-zinc-100 relative overflow-hidden font-mono">

      {/* Fond grille */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(245,158,11,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.4) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
      }} />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_50%_20%,rgba(245,158,11,0.05),transparent_60%)]" />
      </div>

      <Header />

      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <section className="relative pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>

            {/* Bande ENGINE LAB + routing client discret */}
            <div className="flex items-center justify-between mb-10">
              <div className="flex items-center gap-3">
                <div className="h-3.5 w-px bg-amber-500" />                
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[9px] uppercase tracking-widest text-zinc-200">Réseau Partenaires · Échange</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/auth/login')}
                className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-zinc-200 hover:text-amber-400 transition-colors border border-zinc-800/60 hover:border-amber-500/30 px-3 py-1.5 rounded-lg"
              >
                <BookOpen size={11} />
                Vous êtes client ? Par ici
                <ArrowRight size={10} />
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-end">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/8 border border-amber-500/20 rounded-full mb-6">
                  <span className="text-[10px] uppercase tracking-widest text-amber-200/80">Échange de valeur structuré</span>
                </div>

                <h1 className="text-5xl lg:text-6xl font-black uppercase tracking-tight mb-5 leading-none">
                  Créer
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600/60 italic font-serif">
                    le lien.
                  </span>
                </h1>

                <p className="text-zinc-300 leading-relaxed mb-2 text-sm max-w-lg" style={{ fontFamily: 'Georgia, serif' }}>
                  Un partenariat PESACORE n'est pas une remise commerciale. C'est un échange de valeur formalisé — chaque partie apporte quelque chose de mesurable que l'argent seul ne peut pas produire.
                </p>
                <p className=" text-zinc-300 text-xs mt-3" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                  Méthodologie éprouvée depuis 2011 · FSE · Mission Locale · 50% de remise en mouvement observé
                </p>                
              </div>

              {/* Principe de l'échange */}
              <div className="bg-zinc-950/60 border border-zinc-800/60 rounded-xl p-5 space-y-3">
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-200 mb-3">Principe de l'échange</div>
                {[
                  { a: 'Remise ponctuelle',                         b: 'Offre commerciale',          isPartnership: false },
                  { a: 'Réduction durable sans contrepartie',       b: 'Offre commerciale déguisée', isPartnership: false },
                  { a: 'Échange structuré de valeur non-monétaire', b: 'Partenariat',                isPartnership: true  },
                  { a: 'Financement tiers — FSE, fondation, OPCO',  b: 'Partenariat institutionnel', isPartnership: true  },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800/30 last:border-0">
                    <span className="text-[10px] text-zinc-100 font-mono">{item.a}</span>
                    <span className={`text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${
                      item.isPartnership
                        ? 'text-emerald-400 border-emerald-500/20 bg-emerald-500/8'
                        : ' text-zinc-300 border-zinc-800/40 bg-zinc-900/40'
                    }`}>
                      {item.b}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto">

          {/* ── Séparateur ── */}
          <div className="flex items-center gap-4 mb-16">
            <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-zinc-800 to-zinc-800" />
            <div className="flex items-center gap-3 px-2">
              <div className="h-4 w-[1px] bg-amber-500" />
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-200">Protocole_Gage de Confiance</span>
              <div className="h-4 w-[1px] bg-amber-500" />
            </div>
            <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent via-zinc-800 to-zinc-800" />
          </div>

          {/* ── Intro éditoriale — explication claire du concept pour un nouveau visiteur ── */}
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/6 border border-amber-500/15 rounded-full mb-6">
              <Sparkles size={11} className="text-amber-200/70" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-amber-200/60">Qu'est-ce qu'un Pesa ?</span>
            </div>
            <h3 className="text-3xl font-black uppercase tracking-tight text-white mb-6 leading-tight">
              Un gage de confiance,<br />
              <span className="text-amber-200 font-serif italic normal-case tracking-normal font-normal text-4xl">pas un crédit social.</span>
            </h3>
            <p className="text-zinc-400 text-base leading-relaxed font-serif italic mb-4">
              Les Pesas matérialisent l'engagement d'un apprenant dans son parcours. Ils ne mesurent pas ce qu'il est — ils documentent ce qu'il fait. Chaque progression, chaque validation, chaque effort concret se traduit en valeur tangible, échangeable avec les partenaires de l'écosystème.
            </p>
            <p className=" text-zinc-300 text-xs font-mono leading-relaxed uppercase tracking-wider">
              C'est un contrat de confiance tripartite — entre la plateforme, l'apprenant et ses partenaires.
            </p>
          </div>

          {/* ── Dotation initiale — carte aérée ── */}
          <div className="relative mb-16 overflow-hidden border border-amber-500/10 bg-gradient-to-br from-zinc-950 via-zinc-900/40 to-zinc-950 rounded-2xl">
            {/* Halo ambiant */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/4 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2 pointer-events-none" />

            <div className="grid lg:grid-cols-2 gap-0">
              {/* Gauche : chiffre */}
              <div className="p-10 lg:p-14 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-zinc-800/40">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-200 mb-4">Dotation à l'inscription</div>
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-8xl font-black text-amber-200 tracking-tighter leading-none">220</span>
                  <span className="text-sm font-mono text-amber-600/70 uppercase font-bold pb-2">Pesas</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[10px] font-mono text-emerald-500/70 uppercase tracking-widest">Crédités dès la première connexion</span>
                </div>
              </div>

              {/* Droite : explication */}
              <div className="p-10 lg:p-14 flex flex-col justify-center">
                <p className="text-zinc-300 text-sm leading-relaxed font-serif italic mb-6">
                  Ces 220 Pesas sont offerts par les fondateurs à chaque nouvel utilisateur. Ce n'est pas une récompense d'inscription — c'est un premier signal de confiance, envoyé avant même la première action. Une manière de dire : vous avez déjà de la valeur ici, avant d'avoir rien prouvé.
                </p>
                <div className="flex items-start gap-3 p-4 bg-amber-500/4 border border-amber-500/10 rounded-xl">
                  <span className="text-amber-200/60 mt-0.5">↗</span>
                  <p className="text-[11px] font-mono text-amber-200/60 uppercase tracking-wider leading-relaxed">
                    La valeur d'un Pesa est indexée sur l'engagement réel — pas sur le temps passé sur la plateforme. Un parcours accompli compte infiniment plus qu'une heure de navigation passive.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="bg-zinc-950/60 border border-zinc-800/50 rounded-2xl overflow-hidden">

            {/* Onglets */}
            <div className="flex border-b border-zinc-800/50">
              {[
                { id: 'comprendre', label: '01 · Comprendre' },
                { id: 'obtenir',    label: '02 · Acquisition' },
                { id: 'utiliser',   label: '03 · Utilisation' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setPesasTab(tab.id)}
                  className={`flex-1 px-6 py-4 text-[10px] font-mono uppercase tracking-[0.2em] transition-all relative
                    ${pesasTab === tab.id
                      ? 'text-amber-200 bg-zinc-950/80'
                      : ' text-zinc-300 hover:text-zinc-400 hover:bg-zinc-900/30'}`}
                >
                  {tab.label}
                  {pesasTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-amber-600/60 via-amber-500 to-amber-600/60" />
                  )}
                </button>
              ))}
            </div>

            <div className="p-8 lg:p-10">
              <AnimatePresence mode="wait">

                {/* ── TAB : Comprendre ── */}
                {pesasTab === 'comprendre' && (
                  <motion.div key="comprendre"
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <p className=" text-zinc-300 text-xs font-serif italic mb-8 max-w-xl">
                      Les Pesas circulent dans l'écosystème PESACORE entre trois acteurs : la plateforme, les apprenants et les partenaires. Chacun joue un rôle distinct dans ce circuit de valeur.
                    </p>
                    <div className="grid md:grid-cols-2 gap-8">
                      {/* Apprenants */}
                      <div>
                        <div className="flex items-center gap-3 mb-5 pb-3 border-b border-zinc-800/60">
                          <div className="w-2 h-2 rounded-full bg-zinc-600" />
                          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-200">Ce que les apprenants peuvent faire</span>
                        </div>
                        <div className="space-y-2">
                          {PESAS_USAGES.filter(u => u.who === 'Apprenant').map((u, i) => (
                            <div key={i}
                              className="flex items-center gap-4 px-4 py-3 rounded-xl bg-zinc-900/30 border border-zinc-800/30 hover:border-zinc-700/50 hover:bg-zinc-900/60 transition-all group cursor-default"
                            >
                              <span className="text-base grayscale group-hover:grayscale-0 opacity-50 group-hover:opacity-100 transition-all">{u.icon}</span>
                              <span className="text-xs text-zinc-100 font-mono group-hover:text-zinc-200 transition-colors leading-snug">{u.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Partenaires */}
                      <div>
                        <div className="flex items-center gap-3 mb-5 pb-3 border-b border-amber-800/30">
                          <div className="w-2 h-2 rounded-full bg-amber-800/60" />
                          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-800/60">Ce que les partenaires reçoivent</span>
                        </div>
                        <div className="space-y-2">
                          {PESAS_USAGES.filter(u => u.who === 'Partenaire').map((u, i) => (
                            <div key={i}
                              className="flex items-center gap-4 px-4 py-3 rounded-xl bg-amber-500/3 border border-amber-500/8 hover:border-amber-500/20 hover:bg-amber-500/6 transition-all group cursor-default"
                            >
                              <span className="text-base grayscale group-hover:grayscale-0 opacity-50 group-hover:opacity-100 transition-all">{u.icon}</span>
                              <span className="text-xs text-zinc-100 font-mono group-hover:text-zinc-200 transition-colors leading-snug">{u.label}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 px-4 py-3 rounded-xl bg-amber-500/4 border border-amber-500/10">
                          <p className="text-[10px] font-mono text-amber-200/60 leading-relaxed">
                            L'acceptation des Pesas par les partenaires est formalisée par contrat avant tout échange. Ce n'est pas un système ouvert — chaque partenaire valide explicitement sa participation.
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* ── TAB : Acquisition ── */}
                {pesasTab === 'obtenir' && (
                  <motion.div key="obtenir"
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <p className=" text-zinc-300 text-xs font-serif italic mb-8 max-w-xl">
                      Les Pesas s'accumulent par l'action — jamais par la simple présence. Chaque jalon validé dans le parcours génère une dotation proportionnelle à l'effort réel engagé.
                    </p>
                    <div className="space-y-1 rounded-xl overflow-hidden border border-zinc-800/40">
                      {PESAS_COMMENT_OBTENIR.map((item, i) => (
                        <div key={i}
                          className="flex items-center justify-between px-6 py-4 bg-zinc-950/60 hover:bg-zinc-900/60 transition-all group border-b border-zinc-800/30 last:border-0"
                        >
                          <div className="flex items-center gap-4">
                            <span className="text-[9px] font-mono text-zinc-200 w-5">{String(i + 1).padStart(2, '0')}</span>
                            <span className="text-xs text-zinc-100 font-mono group-hover:text-zinc-200 transition-colors uppercase tracking-wider">{item.action}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="h-px w-6 bg-zinc-800 group-hover:w-10 group-hover:bg-amber-500/30 transition-all" />
                            <span className={`text-[11px] font-mono font-bold uppercase tracking-widest px-3 py-1 rounded-lg border ${
                              item.gain.includes('220')
                                ? 'text-amber-400 border-amber-500/20 bg-amber-500/6'
                                : 'text-emerald-400 border-emerald-500/15 bg-emerald-500/4'
                            }`}>
                              {item.gain}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 flex items-center gap-3">
                      <div className="h-px flex-1 bg-zinc-900" />
                      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-200 italic">Accumulation durable · Transmission possible entre utilisateurs</p>
                      <div className="h-px flex-1 bg-zinc-900" />
                    </div>
                  </motion.div>
                )}

                {/* ── TAB : Utilisation ── */}
                {pesasTab === 'utiliser' && (
                  <motion.div key="utiliser"
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <p className=" text-zinc-300 text-xs font-serif italic mb-8 max-w-xl">
                      Une fois accumulés, les Pesas peuvent être mobilisés dans deux directions — vers les ressources de la plateforme pour l'apprenant, ou vers les partenaires pour des accès et des opportunités concrètes.
                    </p>
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Apprenant */}
                      <div className="rounded-xl border border-zinc-800/50 overflow-hidden">
                        <div className="px-5 py-3 bg-zinc-900/40 border-b border-zinc-800/40">
                          <span className="text-[9px] font-mono text-zinc-200 uppercase tracking-[0.3em]">Usage · Apprenant</span>
                        </div>
                        <div className="divide-y divide-zinc-800/30">
                          {[
                            { titre: 'Accès modules avancés', detail: 'Déblocage de contenus d\'investigation et de niveaux d\'analyse supérieurs.' },
                            { titre: 'Règlement stage', detail: 'Contribution partielle ou totale aux frais de stages conventionnés avec les partenaires.' },
                            { titre: 'Certifications', detail: 'Obtention de titres certifiants sans frais monétaires additionnels sur les parcours éligibles.' },
                          ].map((item, i) => (
                            <div key={i} className="px-5 py-4 hover:bg-zinc-900/30 transition-colors group">
                              <div className="text-xs font-bold font-mono text-zinc-300 mb-1 uppercase tracking-wider group-hover:text-white transition-colors">{item.titre}</div>
                              <div className="text-[11px] text-zinc-200 font-serif italic leading-relaxed">{item.detail}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Partenaire */}
                      <div className="rounded-xl border border-amber-500/10 overflow-hidden">
                        <div className="px-5 py-3 bg-amber-500/4 border-b border-amber-500/10">
                          <span className="text-[9px] font-mono text-amber-600/70 uppercase tracking-[0.3em]">Usage · Partenaire</span>
                        </div>
                        <div className="divide-y divide-zinc-800/30">
                          {[
                            { titre: 'Sourcing qualifié', detail: 'Accès prioritaire aux profils affichant un score de confiance élevé — engagés, documentés, traçables.' },
                            { titre: 'Visibilité dans l\'écosystème', detail: 'Mise en avant auprès des apprenants actifs pour attirer des profils correspondant à vos besoins.' },
                            { titre: 'Validation éthique', detail: 'Participation documentée à un système d\'éducation conçu pour éviter la dépendance et maximiser l\'autonomie.' },
                          ].map((item, i) => (
                            <div key={i} className="px-5 py-4 hover:bg-amber-500/4 transition-colors group">
                              <div className="text-xs font-bold font-mono text-amber-200/80 mb-1 uppercase tracking-wider group-hover:text-amber-400 transition-colors">{item.titre}</div>
                              <div className="text-[11px] text-zinc-200 font-serif italic leading-relaxed">{item.detail}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* ── PLUGIN HUB ─────────────────────────────────────────────────────── */}
      <section className="px-6 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1 bg-zinc-800/60" />
            <div className="flex items-center gap-2">
              <div className="h-3 w-px bg-amber-500/30" />
              <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-200">Choisir un protocole d'échange</span>
              <div className="h-3 w-px bg-amber-500/30" />
            </div>
            <div className="h-px flex-1 bg-zinc-800/60" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {PLUGINS.map((plugin, i) => {
              const Icon = plugin.icon;
              const isHovered = hoveredPlugin === plugin.id;
              return (
                <motion.div
                  key={plugin.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  onHoverStart={() => setHoveredPlugin(plugin.id)}
                  onHoverEnd={() => setHoveredPlugin(null)}
                  onClick={() => setActivePlugin(plugin)}
                  className="relative group cursor-pointer"
                >
                  <div className="absolute -inset-px rounded-xl transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                    style={{ background: `radial-gradient(circle at 50% 0%, ${plugin.accentBorder}, transparent 70%)` }} />

                  <div className="relative h-full flex flex-col p-5 bg-zinc-950/80 border rounded-xl transition-all duration-300"
                    style={{
                      borderColor: isHovered ? plugin.accentBorder : 'rgba(39,39,42,0.6)',
                      background: isHovered ? plugin.accentBg : 'rgba(9,9,11,0.8)',
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-200">{plugin.code}</span>
                      <div className={`w-1.5 h-1.5 rounded-full ${isHovered ? '' : 'opacity-30'}`}
                        style={{ background: plugin.accentHex }} />
                    </div>

                    <div className="mb-4 transition-transform duration-300 group-hover:scale-110"
                      style={{ color: isHovered ? plugin.accentHex : '#71717a' }}>
                      <Icon size={28} />
                    </div>

                    <h3 className="text-sm font-black uppercase tracking-tight text-white mb-2 leading-tight">{plugin.label}</h3>

                    <p className="text-zinc-400 text-xs leading-relaxed flex-1 mb-4" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                      {plugin.desc}
                    </p>

                    <div className="text-[8px] font-mono text-zinc-200 mb-3 pb-3 border-b border-zinc-800/40">
                      <span style={{ color: isHovered ? plugin.accentHex : '#71717a' }}>↕</span> {ECHANGE_LABELS[plugin.id].partenaire.split('·')[0].trim()}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-[8px] font-mono text-zinc-200 uppercase">{plugin.protocol}</span>
                      <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider transition-colors"
                        style={{ color: isHovered ? plugin.accentHex : '#71717a' }}>
                        Ouvrir <ChevronRight size={9} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── PARTENAIRES ENGAGÉS ────────────────────────────────────────────── */}
      <section className="px-6 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="border border-zinc-800/40 rounded-xl overflow-hidden bg-zinc-950/30">

            {/* En-tête */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800/40 bg-black/20">
              <div className="flex items-center gap-3">
                <Lock size={13} className="text-amber-300" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-100">
                  Réseau partenaires
                </span>
              </div>
              <span className="text-[9px] font-mono text-zinc-200 uppercase tracking-wider">
                Échange documenté · Qualification mutuelle
              </span>
            </div>

            {/* Ce qu'un partenariat actif produit — trois dimensions */}
            <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-800/30">
              {[
                {
                  code: 'TERRAIN',
                  titre: 'Un accès irremplaçable',
                  corps: "Ce qu'aucun budget ne peut acheter — un public réel, une légitimité institutionnelle, un réseau de prescription. Le terrain est la condition de possibilité du déploiement.",
                  accent: '#10b981',
                },
                {
                  code: 'ÉCHANGE',
                  titre: 'Une valeur symétrique',
                  corps: "Chaque partenariat est structuré autour d'un échange documenté. Ce que PESACORE apporte et ce que le partenaire apporte sont explicités, mesurés et consultables par les deux parties.",
                  accent: '#f59e0b',
                },
                {
                  code: 'DURÉE',
                  titre: 'Une relation qui tient',
                  corps: "Un partenariat PESACORE n'est pas une prestation ponctuelle. Il s'inscrit dans une durée — avec des jalons, des évaluations partagées, et une trace documentée de ce qui s'est passé.",
                  accent: '#22d3ee',
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="px-6 py-6"
                >
                  <div className="text-[9px] font-mono uppercase tracking-widest mb-3"
                    style={{ color: item.accent, opacity: 0.7 }}>
                    {item.code}
                  </div>
                  <div className="text-xs font-black uppercase tracking-tight text-white mb-3">
                    {item.titre}
                  </div>
                  <p className="text-[11px] text-zinc-200 leading-relaxed"
                    style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
                    {item.corps}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Pied — ce qui se passe quand le partenariat est actif */}
            <div className="px-6 py-4 border-t border-zinc-800/40 bg-black/10 flex items-center justify-between gap-6">
              <p className="text-[9px] font-mono text-zinc-200 italic">
                L'échange de valeur de chaque partenariat est documenté et consultable par les parties.
                Accès sur identifiants transmis lors de la qualification.
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                <span className="text-[9px] font-mono text-amber-200 uppercase tracking-wider">
                  Réseau en constitution
                </span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6 bg-slate-950/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-[10px] font-mono text-zinc-200 uppercase tracking-widest">
          <span>© 2026 · PESACORE Architect : Tous droits réservés.</span>
          <div className="flex items-center gap-2">
            <div className="h-3 w-px bg-amber-500/30" />
            <span className="text-[9px] text-amber-200 tracking-[0.2em]">ENGINE LAB</span>
            <div className="h-3 w-px bg-amber-500/30" />
          </div>
          <button onClick={() => navigate('/')} className="hover:text-zinc-300 transition-colors">Accueil</button>
        </div>
      </footer>

      {/* Wizard overlay */}
      <AnimatePresence>
        {activePlugin && (
          <PluginWizard
            plugin={activePlugin}
            onClose={() => setActivePlugin(null)}
            navigate={navigate}
          />
        )}
      </AnimatePresence>
    </div>
  );
}